import { useCurrentSite } from "../../../contexts/models/SiteContext";
import { isEmptySlateDocument } from "../../../core/constants";
import {
  Configuration,
  RegistryDetails,
  SectionTypes,
} from "../../../resources/modelTypes";
import { SectionComponentProps } from "../../types";
import { DisplayPhoto } from "../DisplayPhoto";
import { useWebsiteWidth } from "../WebsiteWidthContext";
import { ThemedRichText } from "./ThemedRichText";
import { css } from "@emotion/react";
import { SmallHeadline, TinyHeadline } from "@minted/fancyclothes";
import { Button as FancyButton } from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo } from "react";

const styles = {
  column: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
    width: 100%;
  `,
  image: css`
    aspect-ratio: 1;
    height: ${styleUtils.rem(120)};
    width: ${styleUtils.rem(120)};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    min-height: ${styleUtils.rem(120)};
    min-width: ${styleUtils.rem(120)};
    max-height: ${styleUtils.rem(120)};
    max-width: ${styleUtils.rem(120)};
  `,
  marginLeft: css`
    margin-left: ${theme.spacing.x4};
  `,
  // We do not want to inherit the page text alignment properties.
  textAlignment: (isMobile: boolean) => css`
    text-align: ${isMobile ? "center" : "left"}; !important;
  `,
  wrapper: (isMobile: boolean) => css`
    align-items: center;
    display: flex;
    flex-direction: ${isMobile ? "column" : "row"};
    gap: ${theme.spacing.x4};
    justify-content: flex-start;
    padding-top: ${isMobile ? theme.spacing.x4 : theme.spacing.x8};
    padding-bottom: ${isMobile ? theme.spacing.x4 : theme.spacing.x8};
    width: 100%;
    box-sizing: border-box;
    max-width: ${styleUtils.rem(728)};
    margin-left: auto;
    margin-right: auto;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      max-width: ${styleUtils.rem(335)};
      flex-direction: column;
      padding: ${theme.spacing.x4};
    `)};
  `,
};

type DisplayRegistrySectionProps = {
  configuration: Configuration;
} & SectionComponentProps;

const DisplayRegistrySection = ({
  configuration,
  section,
}: DisplayRegistrySectionProps) => {
  const site = useCurrentSite();
  const { isWedding } = site;
  const details = section.sectionDetails as RegistryDetails;

  const width = useWebsiteWidth();
  const isMobile: boolean = width !== null && width < 600;

  return (
    <div css={styles.wrapper(isMobile)}>
      {(section.photo || section.image) && (
        <div css={styles.image}>
          <DisplayPhoto
            configuration={configuration}
            isRegistryPhoto
            section={section}
          />
        </div>
      )}

      <div css={styles.column}>
        {details.title && (
          <div css={styles.textAlignment(isMobile)}>
            {isWedding ? (
              <SmallHeadline>{details.title}</SmallHeadline>
            ) : (
              <TinyHeadline>{details.title}</TinyHeadline>
            )}
          </div>
        )}

        {!isEmptySlateDocument(details.content) && (
          <ThemedRichText content={details.content} />
        )}

        {section.sectionType === SectionTypes.CashRegistry &&
          details.content && (
            <ThemedRichText
              content={[
                {
                  align: "left",
                  type: "paragraph",
                  // @ts-expect-error Will be removed once WW-5224 gets merged because it has a different ui for both registry types.
                  children: [{ text: details.content }],
                },
              ]}
            />
          )}
      </div>

      {details.websiteUrl && (
        // The margin left will offset the centered button on mobile
        // and we need the margin on desktop
        <div css={!isMobile && styles.marginLeft}>
          <FancyButton
            text={details.buttonText || "View Website"}
            url={details.websiteUrl}
          />
        </div>
      )}
    </div>
  );
};

const MemoizedDisplayRegistrySection = memo(DisplayRegistrySection);

export { MemoizedDisplayRegistrySection as DisplayRegistrySection };
